import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, LogoMark } from "@/components/Brand";
import { useForceLightSurface } from "@/context/ThemeContext";

const Shell = ({ title, subtitle, children, footer }) => {
  useForceLightSurface();
  return (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F9FA]">
    <div className="hidden lg:flex flex-col justify-between ink p-12 text-white relative">
      <div className="absolute inset-0 hair-dark opacity-60" />
      <Link to="/" className="relative" data-testid="auth-brand-link">
        <Logo tone="dark" />
      </Link>
      <div className="relative max-w-md">
        <p className="overline text-white/45">Secure workspace</p>
        <h2 className="font-display text-4xl leading-tight tracking-tight mt-5">
          Your business, your data, your permissions.
        </h2>
        <p className="text-sm text-white/60 mt-6 leading-relaxed">
          Access is resolved on the server from your role, business membership and individual
          permissions — the same for every sign-in method.
        </p>
      </div>
      <p className="relative text-xs text-white/40">Developed by DigiTide</p>
    </div>
    <div className="flex flex-col justify-center min-h-screen lg:min-h-0 p-6 sm:p-12">
      <div className="w-full max-w-md mx-auto fade-up">
        <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-sm text-slate-500 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        {/* Mobile-only logo */}
        <div className="lg:hidden mb-6" data-testid="auth-mobile-logo">
          <LogoMark className="h-10 w-10" />
        </div>
        <p className="overline text-[#EA580C]">{subtitle}</p>
        <h1 className="font-ui text-2xl font-bold tracking-tight mt-3">{title}</h1>
        <div className="mt-8">{children}</div>
        {footer && <div className="mt-8 text-sm text-slate-500">{footer}</div>}
      </div>
    </div>
  </div>
  );
};

const field = "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-900";
const primaryBtn = "w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] px-6 py-3 text-sm font-medium text-white hover:bg-[#1E293B] hover:text-white disabled:opacity-60";

export function LoginPage() {
  const { login, verifyOtp, resendOtp, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (user) navigate(user.role === "platform_owner" ? "/owner" : "/app", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!cooldown) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return; // prevent duplicate login requests while a submission is in flight
    setBusy(true);
    setError("");
    try {
      const data = await login(email.trim(), password);
      setChallenge(data);
      setCooldown(data.resend_after || 60);
      setPassword("");
      toast.success(data.email_sent
        ? `We emailed a sign-in code to ${email.trim()}`
        : "Code generated, but the email could not be sent. Contact your administrator.");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (busy) return; // prevent duplicate OTP submissions while verification is in flight
    setBusy(true);
    setError("");
    try {
      const u = await verifyOtp(challenge.challenge_id, otp.trim());
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "platform_owner" ? "/owner" : "/app", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError("");
    try {
      const data = await resendOtp(challenge.challenge_id);
      setChallenge(data);
      setCooldown(data.resend_after || 60);
      setOtp("");
      toast.success("A new code is on its way.");
    } catch (err) { setError(apiError(err)); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (challenge) {
    return (
      <Shell subtitle="Two-step sign in" title="Enter your email code"
        footer={<button onClick={() => { setChallenge(null); setOtp(""); setError(""); }}
          className="text-slate-900 underline" data-testid="otp-back-btn">Use a different account</button>}>
        <form onSubmit={verify} className="space-y-5" data-testid="otp-form">
          <p className="text-sm text-slate-600">
            We sent a 6-digit code to <strong>{challenge.email}</strong>. It expires in 10 minutes
            and can be used once.
          </p>
          <label className="block">
            <span className="overline text-slate-400">Sign-in code</span>
            <input data-testid="otp-input" required inputMode="numeric" autoFocus value={otp}
              maxLength={6} data-enter-next="false"
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className={`mt-2 ${field} tracking-[0.5em] text-center text-lg`} placeholder="------" />
          </label>
          {error && <p className="text-sm text-red-600" data-testid="otp-error">{error}</p>}
          <button type="submit" disabled={busy || otp.length < 6} className={primaryBtn} data-testid="otp-verify-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify &amp; continue
          </button>
          <button type="button" onClick={resend} disabled={cooldown > 0} data-testid="otp-resend-btn"
            className="w-full rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 hover:border-slate-900 disabled:opacity-50">
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell subtitle="Sign in" title="Access your workspace"
      footer={<span>Forgot your password? <Link to="/forgot-password" className="text-slate-900 underline" data-testid="forgot-password-link">Reset it securely</Link></span>}>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <label className="block">
          <span className="overline text-slate-400">Email</span>
          <input data-testid="login-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} className={`mt-2 ${field}`} placeholder="you@business.com" />
        </label>
        <label className="block">
          <span className="overline text-slate-400">Password</span>
          <input data-testid="login-password-input" type="password" required value={password}
            data-enter-next="false"
            onChange={(e) => setPassword(e.target.value)} className={`mt-2 ${field}`} placeholder="••••••••" />
        </label>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} className={primaryBtn} data-testid="login-submit-btn">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Login
        </button>
      </form>
      <div className="my-7 flex items-center gap-4 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> OR <span className="h-px flex-1 bg-slate-200" />
      </div>
      <button type="button" onClick={google} data-testid="google-login-btn"
        className="w-full rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900">
        Continue with Google
      </button>
      <p className="mt-4 text-xs text-slate-400 leading-relaxed">
        Google sign-in verifies your identity only. Access requires an authorised AJ Classic
        Healthcare account with a role and permissions.
      </p>
    </Shell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(data.message);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell subtitle="Password reset" title="Forgot your password?"
      footer={<Link to="/login" className="text-slate-900 underline">Back to login</Link>}>
      {sent ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800" data-testid="forgot-success">
          {sent}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <label className="block">
            <span className="overline text-slate-400">Registered email</span>
            <input data-testid="forgot-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className={`mt-2 ${field}`} />
          </label>
          {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
          <button type="submit" disabled={busy} className={primaryBtn} data-testid="forgot-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Both passwords must match.");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell subtitle="Create password" title="Set a new password"
      footer={<Link to="/login" className="text-slate-900 underline">Back to login</Link>}>
      {!token ? (
        <p className="text-sm text-red-600" data-testid="reset-missing-token">
          This reset link is incomplete. Please request a new link from the Forgot Password page.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <label className="block">
            <span className="overline text-slate-400">New password</span>
            <input data-testid="reset-password-input" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} className={`mt-2 ${field}`} />
          </label>
          <label className="block">
            <span className="overline text-slate-400">Confirm password</span>
            <input data-testid="reset-confirm-input" type="password" required value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className={`mt-2 ${field}`} />
          </label>
          {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy} className={primaryBtn} data-testid="reset-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </button>
        </form>
      )}
    </Shell>
  );
}

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId },
          { headers: { "X-Session-ID": sessionId } });
        window.history.replaceState(null, "", "/app");
        window.location.href = data.user.role === "platform_owner" ? "/owner" : "/app";
      } catch (e) {
        setError(apiError(e));
      }
    })();
  }, []);

  return (
    <div className="min-h-screen grid place-items-center bg-[#F8F9FA] px-6">
      {error ? (
        <div className="max-w-md text-center" data-testid="google-auth-error">
          <p className="font-ui font-semibold text-slate-900">Google sign-in blocked</p>
          <p className="text-sm text-slate-600 mt-3">{error}</p>
          <button onClick={() => navigate("/login")} className="mt-6 rounded-full bg-[#0F172A] px-6 py-2.5 text-sm text-white hover:bg-[#1E293B] hover:text-white">
            Back to login
          </button>
        </div>
      ) : (
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      )}
    </div>
  );
}

export function LegalPage({ kind }) {
  const isPrivacy = kind === "privacy";
  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 mb-10">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <p className="overline text-[#EA580C]">{isPrivacy ? "Privacy" : "Terms"}</p>
        <h1 className="font-display text-4xl tracking-tight mt-4">
          {isPrivacy ? "Privacy Policy" : "Terms & Conditions"}
        </h1>
        <p className="text-sm text-slate-500 mt-4">
          This document is prepared for legal review and is not a statement of certified compliance.
        </p>
        <div className="mt-10 space-y-8 text-sm text-slate-600 leading-relaxed">
          {(isPrivacy
            ? [
                ["Data we process", "Patient, staff and business records entered by authorised users of your business, including names, contact numbers, clinical notes, billing and inventory data."],
                ["Purpose & consent", "Data is processed solely to operate your hospital or pharmacy workflows. Patient contact details are used for operational notifications you configure."],
                ["Third-party processors", "Transactional email is delivered through a managed email provider. Patient notifications are delivered through the WhatsApp Business Cloud API when you configure credentials. No data is sold or shared for advertising."],
                ["Storage & access control", "Records are stored against your business identifier. Access is enforced server-side by role and individual permission. Sensitive actions are recorded in an audit log."],
                ["Your rights", "Business administrators may request correction, export or deletion of records. Deletion requests for financial records are honoured through reversal entries so audit history remains intact."],
                ["DPDP Act 2023 readiness", "Consent records, purpose limitation, access control, audit logging and breach-response processes are supported. Final compliance requires your own legal review and configuration."],
              ]
            : [
                ["Use of platform", "Accounts are issued by the Platform Owner or your business administrator. You are responsible for actions performed under your credentials."],
                ["Business isolation", "Each business is a separate tenant. Attempting to access another business's data is a breach of these terms and is blocked and logged."],
                ["Financial records", "Completed invoices cannot be deleted. Corrections are made through void, cancel, return or adjustment entries, each requiring explicit permission."],
                ["Clinical responsibility", "The platform records clinical and pharmacy information entered by your staff. Medical decisions and regulatory obligations remain with your registered practitioners."],
                ["Notifications", "Email is used for authentication and security. WhatsApp is used for operational patient notifications where you have configured credentials and templates."],
                ["Availability & changes", "We may update features and these terms. Continued use after an update constitutes acceptance."],
              ]
          ).map(([t, b]) => (
            <section key={t}>
              <h2 className="font-ui text-lg font-bold tracking-tight text-slate-900">{t}</h2>
              <p className="mt-3">{b}</p>
            </section>
          ))}
        </div>
        <p className="mt-14 text-xs text-slate-400">Developed by DigiTide</p>
      </div>
    </div>
  );
}
